import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ClientDashboardTabs from '../../components/navigation/ClientDashboardTabs';
import ProjectCard from './components/ProjectCard';
import ProjectModal from './components/ProjectModal';
import BulkActionsBar from './components/BulkActionsBar';
import FilterBar from './components/FilterBar';
import StatsCards from './components/StatsCards';

const GalleryProjects = () => {
  const navigate = useNavigate();
  const { companyIdentifier } = useParams();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFloorType, setSelectedFloorType] = useState('');
  const [selectedRoom, setSelectedRoom] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [viewMode, setViewMode] = useState('grid');

  const [projects, setProjects] = useState([
  {
    id: 1,
    title: "Modern Kitchen Metallic Epoxy",
    description: "Stunning metallic epoxy installation in contemporary kitchen with high-gloss finish",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c9889b36-1765159449018.png",
    imageAlt: "Modern kitchen with glossy metallic epoxy flooring in silver and gray tones with contemporary white cabinets",
    floorType: "Metallic Epoxy",
    room: "Kitchen",
    tags: ["Modern", "Residential", "High-gloss", "Silver"],
    isFeatured: true,
    createdAt: "2025-11-20"
  },
  {
    id: 2,
    title: "Garage Floor Flake System",
    description: "Durable flake epoxy system for residential garage with excellent chip resistance",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_193ee2927-1764798924046.png",
    imageAlt: "Residential garage with gray flake epoxy flooring showing speckled pattern and matte finish",
    floorType: "Flake Epoxy",
    room: "Garage",
    tags: ["Residential", "Durable", "Gray", "Matte"],
    isFeatured: true,
    createdAt: "2025-11-18"
  },
  {
    id: 3,
    title: "Commercial Showroom Floor",
    description: "High-traffic commercial epoxy with custom color blend for automotive showroom",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fe49e850-1765763743651.png",
    imageAlt: "Commercial automotive showroom with glossy black epoxy flooring reflecting overhead lights",
    floorType: "Solid Color Epoxy",
    room: "Commercial Space",
    tags: ["Commercial", "High-traffic", "Black", "Glossy"],
    isFeatured: false,
    createdAt: "2025-11-15"
  },
  {
    id: 4,
    title: "Basement Living Area",
    description: "Warm-toned metallic epoxy creating inviting atmosphere in finished basement",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_178d9e7cf-1764638986788.png",
    imageAlt: "Finished basement living area with warm bronze metallic epoxy flooring and modern furniture",
    floorType: "Metallic Epoxy",
    room: "Basement",
    tags: ["Residential", "Warm-tones", "Bronze", "Living-space"],
    isFeatured: false,
    createdAt: "2025-11-12"
  },
  {
    id: 5,
    title: "Restaurant Kitchen Floor",
    description: "Food-safe epoxy system with anti-slip properties for commercial kitchen",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1250f089d-1764903601935.png",
    imageAlt: "Commercial restaurant kitchen with light gray anti-slip epoxy flooring and stainless steel equipment",
    floorType: "Anti-Slip Epoxy",
    room: "Commercial Kitchen",
    tags: ["Commercial", "Food-safe", "Anti-slip", "Gray"],
    isFeatured: true,
    createdAt: "2025-11-10"
  },
  {
    id: 6,
    title: "Home Office Workspace",
    description: "Clean white epoxy with subtle flake pattern for professional home office",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14316be1a-1764793771309.png",
    imageAlt: "Modern home office with white flake epoxy flooring and minimalist desk setup",
    floorType: "Flake Epoxy",
    room: "Office",
    tags: ["Residential", "White", "Professional", "Subtle"],
    isFeatured: false,
    createdAt: "2025-11-08"
  },
  {
    id: 7,
    title: "Retail Store Entrance",
    description: "Eye-catching metallic blend creating welcoming entrance for retail space",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fde8d284-1766029957550.png",
    imageAlt: "Retail store entrance with copper and gold metallic epoxy flooring creating warm welcoming atmosphere",
    floorType: "Metallic Epoxy",
    room: "Retail Space",
    tags: ["Commercial", "Metallic", "Copper", "Entrance"],
    isFeatured: false,
    createdAt: "2025-11-05"
  },
  {
    id: 8,
    title: "Luxury Bathroom Suite",
    description: "High-end metallic epoxy with pearl finish for upscale bathroom renovation",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fe49e850-1765763743651.png",
    imageAlt: "Luxury bathroom with pearl white metallic epoxy flooring and modern fixtures",
    floorType: "Metallic Epoxy",
    room: "Bathroom",
    tags: ["Residential", "Luxury", "Pearl", "High-end"],
    isFeatured: true,
    createdAt: "2025-11-02"
  }]
  );

  const floorTypes = ["Metallic Epoxy", "Flake Epoxy", "Solid Color Epoxy", "Anti-Slip Epoxy"];
  const rooms = ["Kitchen", "Garage", "Basement", "Office", "Bathroom", "Commercial Space", "Commercial Kitchen", "Retail Space"];

  const allTags = useMemo(() => {
    const tagSet = new Set();
    projects?.forEach((project) => {
      project?.tags?.forEach((tag) => tagSet?.add(tag));
    });
    return Array.from(tagSet)?.sort();
  }, [projects]);

  const filteredProjects = useMemo(() => {
    return projects?.filter((project) => {
      const matchesSearch = !searchTerm ||
      project?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      project?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase());

      const matchesFloorType = !selectedFloorType || project?.floorType === selectedFloorType;
      const matchesRoom = !selectedRoom || project?.room === selectedRoom;
      const matchesTag = !selectedTag || project?.tags?.includes(selectedTag);

      return matchesSearch && matchesFloorType && matchesRoom && matchesTag;
    });
  }, [projects, searchTerm, selectedFloorType, selectedRoom, selectedTag]);

  const stats = useMemo(() => ({
    total: projects?.length,
    featured: projects?.filter((p) => p?.isFeatured)?.length,
    floorTypes: new Set(projects.map((p) => p.floorType))?.size,
    rooms: new Set(projects.map((p) => p.room))?.size
  }), [projects]);

  const handleAddProject = () => {
    setEditingProject(null);
    setIsModalOpen(true);
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setIsModalOpen(true);
  };

  const handleSaveProject = (projectData) => {
    if (editingProject) {
      setProjects((prev) => prev?.map((p) => p?.id === editingProject?.id ? { ...projectData, id: p?.id } : p));
    } else {
      const newProject = {
        ...projectData,
        id: Math.max(...projects?.map((p) => p?.id)) + 1,
        createdAt: new Date()?.toISOString()?.split('T')?.[0]
      };
      setProjects((prev) => [newProject, ...prev]);
    }
    setIsModalOpen(false);
    setEditingProject(null);
  };

  const handleDeleteProject = (id) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      setProjects((prev) => prev?.filter((p) => p?.id !== id));
      setSelectedProjects((prev) => prev?.filter((pid) => pid !== id));
    }
  };

  const handleToggleFeatured = (id) => {
    setProjects((prev) => prev?.map((p) =>
    p?.id === id ? { ...p, isFeatured: !p?.isFeatured } : p
    ));
  };

  const handleSelectProject = (id) => {
    setSelectedProjects((prev) =>
    prev?.includes(id) ? prev?.filter((pid) => pid !== id) : [...prev, id]
    );
  };

  const handleFeatureAll = () => {
    setProjects((prev) => prev?.map((p) =>
    selectedProjects?.includes(p?.id) ? { ...p, isFeatured: true } : p
    ));
    setSelectedProjects([]);
  };

  const handleUnfeatureAll = () => {
    setProjects((prev) => prev?.map((p) =>
    selectedProjects?.includes(p?.id) ? { ...p, isFeatured: false } : p
    ));
    setSelectedProjects([]);
  };

  const handleDeleteAll = () => {
    if (window.confirm(`Are you sure you want to delete ${selectedProjects?.length} projects?`)) {
      setProjects((prev) => prev?.filter((p) => !selectedProjects?.includes(p?.id)));
      setSelectedProjects([]);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedFloorType('');
    setSelectedRoom('');
    setSelectedTag('');
  };

  useEffect(() => {
    // If accessed via company-specific route, load company data
    if (companyIdentifier) {
      const loadCompanyData = async () => {
        try {
          setLoading(true);
          // Extract slug from compound identifier
          const extractSlugFromIdentifier = (identifier) => {
            const parts = identifier?.split('-');
            let uuidStartIndex = -1;
            for (let i = parts?.length - 1; i >= 0; i--) {
              if (parts?.[i]?.length === 12 && /^[a-f0-9]+$/i?.test(parts?.[i])) {
                if (i >= 4 &&
                parts?.[i - 1]?.length === 4 && /^[a-f0-9]+$/i?.test(parts?.[i - 1]) &&
                parts?.[i - 2]?.length === 4 && /^[a-f0-9]+$/i?.test(parts?.[i - 2]) &&
                parts?.[i - 3]?.length === 4 && /^[a-f0-9]+$/i?.test(parts?.[i - 3]) &&
                parts?.[i - 4]?.length === 8 && /^[a-f0-9]+$/i?.test(parts?.[i - 4])) {
                  uuidStartIndex = i - 4;
                  break;
                }
              }
            }
            if (uuidStartIndex > 0) {
              return parts?.slice(0, uuidStartIndex)?.join('-');
            }
            return identifier;
          };

          const slug = extractSlugFromIdentifier(companyIdentifier);
          const normalizedSlug = decodeURIComponent(slug || '')?.toLowerCase()?.trim();

          // TODO: Implement companyService or fetch company data
          const companyData = null; // Placeholder until companyService is implemented
          setCompany(companyData);
        } catch (error) {
          console.error('Error loading company data:', error);
        } finally {
          setLoading(false);
        }
      };

      loadCompanyData();
    } else {
      setLoading(false);
    }
  }, [companyIdentifier]);

  return (
    <div className="min-h-screen bg-background">
      {companyIdentifier && <ClientDashboardTabs />}
      <div className={companyIdentifier ? "main-content-with-tabs" : ""}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-text-primary mb-2">Gallery & Projects</h1>
              <p className="text-text-secondary">Showcase your completed installations and inspire customers</p>
            </div>
            <Button
              variant="default"
              onClick={handleAddProject}
              iconName="Plus"
              iconPosition="left"
              iconSize={18}>

              Add Project
            </Button>
          </div>

          <StatsCards stats={stats} />

          <FilterBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            selectedFloorType={selectedFloorType}
            onFloorTypeChange={setSelectedFloorType}
            selectedRoom={selectedRoom}
            onRoomChange={setSelectedRoom}
            selectedTag={selectedTag}
            onTagChange={setSelectedTag}
            floorTypes={floorTypes}
            rooms={rooms}
            tags={allTags}
            onClearFilters={handleClearFilters} />


          <div className="flex items-center justify-between">
            <p className="text-sm text-text-secondary">
              Showing {filteredProjects?.length} of {projects?.length} projects
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
                iconName="Grid3x3"
                iconSize={16} />

              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                iconName="List"
                iconSize={16} />

            </div>
          </div>

          {filteredProjects?.length === 0 ?
          <div className="bg-card border border-border rounded-lg p-12 text-center">
              <Icon name="Image" size={48} className="mx-auto mb-4 text-text-secondary" />
              <h3 className="text-lg font-semibold text-text-primary mb-2">No projects found</h3>
              <p className="text-text-secondary mb-6">
                {searchTerm || selectedFloorType || selectedRoom || selectedTag ?
              'Try adjusting your filters' : 'Start building your gallery by adding your first project'}
              </p>
              {!searchTerm && !selectedFloorType && !selectedRoom && !selectedTag &&
            <Button
              variant="default"
              onClick={handleAddProject}
              iconName="Plus"
              iconPosition="left"
              iconSize={16}>

                  Add Your First Project
                </Button>
            }
            </div> :

          <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'
          }>
              {filteredProjects?.map((project) =>
            <ProjectCard
              key={project?.id}
              project={project}
              onEdit={handleEditProject}
              onDelete={handleDeleteProject}
              onToggleFeatured={handleToggleFeatured}
              isSelected={selectedProjects?.includes(project?.id)}
              onSelect={handleSelectProject} />

            )}
            </div>
          }
        </div>
      </div>
      <ProjectModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingProject(null);
        }}
        onSave={handleSaveProject}
        project={editingProject}
        floorTypes={floorTypes}
        rooms={rooms} />

      <BulkActionsBar
        selectedCount={selectedProjects?.length}
        onFeatureAll={handleFeatureAll}
        onUnfeatureAll={handleUnfeatureAll}
        onDeleteAll={handleDeleteAll}
        onClearSelection={() => setSelectedProjects([])} />

    </div>);

};

export default GalleryProjects;